// List prices (USD per 1M tokens) from provider docs, May 2026.
// Thinking levels share the same $/token rate; they change reasoning depth and output volume.

export type TokenRates = { input: number; output: number; apiModel?: string };

export const TOKEN_RATES = {
  claude: { input: 5, output: 25, apiModel: 'claude-opus-4-7' },
  claudeFast: { input: 30, output: 150 },
  codex: { input: 5, output: 30, apiModel: 'gpt-5.5' },
  gemini: { input: 1.5, output: 9, apiModel: 'gemini-3.5-flash' },
  cerebras: { input: 0.6, output: 1.2, apiModel: 'qwen-3-235b-a22b-instruct-2507' },
  cursorStandard: { input: 0.5, output: 2.5, apiModel: 'composer-2.5' },
  cursorFast: { input: 3, output: 15 },
  grok: { input: 1, output: 2, apiModel: 'grok-build-0.1' },
} as const;

/** Adaptive / reasoning effort — same $/token; more effort → more output (reasoning) tokens. */
export const THINKING_EFFORT: Record<string, string> = {
  low: 'Minimal thinking; skips for simple tasks (lowest tokens).',
  medium: 'Moderate thinking; may skip for very simple queries.',
  high: 'Always thinks (default). Deep reasoning on complex tasks.',
  xhigh: 'Extended exploration — long agentic/coding runs (Opus 4.7, Codex).',
  max: 'Unconstrained depth; highest token use (~2.7× vs xhigh on Opus). Grok runs at max.',
};

function fmtUsd(n: number): string {
  return n < 1 ? `$${n.toFixed(2)}` : `$${n.toFixed(2).replace(/\.00$/, '')}`;
}

export function formatTokenRates(r: TokenRates): string {
  return `Input ${fmtUsd(r.input)} / Output ${fmtUsd(r.output)} per 1M tokens`;
}

/** Single-line string for CSS `data-tip` (instant hover tooltips). */
export function pricingTipDataAttr(text: string): string {
  return text.replace(/\n/g, ' · ');
}

type AltYModelId = 'claude' | 'gemini' | 'codex' | 'cerebras' | 'cursor' | 'grok';

function parseOption(value: string): { model: AltYModelId; thinking?: string } | null {
  const [m, t] = value.split(':');
  if (m !== 'claude' && m !== 'gemini' && m !== 'codex' && m !== 'cerebras' && m !== 'cursor' && m !== 'grok') {
    return null;
  }
  return { model: m, thinking: t || undefined };
}

function baseRates(model: AltYModelId): TokenRates {
  switch (model) {
    case 'claude': return TOKEN_RATES.claude;
    case 'codex': return TOKEN_RATES.codex;
    case 'gemini': return TOKEN_RATES.gemini;
    case 'cerebras': return TOKEN_RATES.cerebras;
    case 'cursor': return TOKEN_RATES.cursorStandard;
    case 'grok': return TOKEN_RATES.grok;
  }
}

function fastMaxLines(model: AltYModelId, fast: boolean, maxMode: boolean): string[] {
  const lines: string[] = [];
  if (model === 'claude') {
    lines.push(
      fast
        ? `Fast: ${formatTokenRates(TOKEN_RATES.claudeFast)} (6× standard, ~2.5× faster).`
        : 'Fast (cog/header when primary): 6× — $30 / $150 per 1M.',
    );
  }
  if (model === 'codex') {
    lines.push(
      fast
        ? 'Fast: 2.5× Codex credits on GPT-5.5 (~1.5× faster); same API $/token if billed via API key.'
        : 'Fast (cog): 2.5× Codex credits on GPT-5.5 (~1.5× faster).',
    );
  }
  if (model === 'cursor') {
    lines.push(
      fast
        ? `Fast: ${formatTokenRates(TOKEN_RATES.cursorFast)} (standard: ${formatTokenRates(TOKEN_RATES.cursorStandard)}).`
        : `Fast (bolt): ${formatTokenRates(TOKEN_RATES.cursorFast)} vs standard ${formatTokenRates(TOKEN_RATES.cursorStandard)}.`,
    );
    lines.push(
      maxMode
        ? 'Max mode: full context window; same $/token, more tokens per turn.'
        : 'Max mode (cog): extends context to model max; same $/token, higher total per turn.',
    );
  }
  return lines;
}

/** Tooltip for a model[:thinking] picker value. */
export function modelOptionPricingTooltip(
  value: string,
  options?: { fast?: boolean; maxMode?: boolean },
): string {
  const parsed = parseOption(value);
  if (!parsed) return '';

  const lines: string[] = [];
  const rates = baseRates(parsed.model);
  lines.push(formatTokenRates(rates));
  if (rates.apiModel) lines.push(`API: ${rates.apiModel}`);

  if (parsed.thinking && THINKING_EFFORT[parsed.thinking]) {
    lines.push(`Effort (${parsed.thinking}): ${THINKING_EFFORT[parsed.thinking]}`);
  } else if (parsed.model === 'gemini') {
    lines.push('Thinking: high (fixed for Gemini in Alt-Y).');
  } else if (parsed.model === 'grok') {
    lines.push(`Effort: ${THINKING_EFFORT.max}`);
  } else if (parsed.model === 'cerebras') {
    lines.push('Non-thinking instruct model; no effort levels.');
  } else if (parsed.model === 'cursor') {
    lines.push('No effort picker; Composer 2.5 uses Cursor adaptive routing.');
  }

  if (parsed.model === 'codex' && !parsed.thinking) {
    lines.push('Pick a thinking level — higher levels bill more output (reasoning) tokens at the same $/token.');
  }

  lines.push(...fastMaxLines(parsed.model, !!options?.fast, !!options?.maxMode));

  return lines.join('\n');
}

export function claudeFastModeTooltip(): string {
  return [
    'Claude Opus fast mode (research preview)',
    formatTokenRates(TOKEN_RATES.claudeFast),
    '6× standard Opus 4.7 rates · ~2.5× faster output',
    formatTokenRates(TOKEN_RATES.claude),
  ].join('\n');
}

export function codexFastModeTooltip(): string {
  return [
    'Codex fast mode (GPT-5.5)',
    formatTokenRates(TOKEN_RATES.codex),
    '2.5× Codex credit rate · ~1.5× faster (ChatGPT/Codex subscription)',
  ].join('\n');
}

export function cursorFastModeTooltip(): string {
  return [
    'Composer 2.5 fast tier',
    formatTokenRates(TOKEN_RATES.cursorFast),
    `Standard tier: ${formatTokenRates(TOKEN_RATES.cursorStandard)}`,
  ].join('\n');
}

export function cursorMaxModeTooltip(): string {
  return [
    'Cursor Max mode',
    'Uses the model’s maximum context window (deeper codebase awareness).',
    'Same $/token as standard; each request uses more tokens.',
    formatTokenRates(TOKEN_RATES.cursorStandard),
  ].join('\n');
}
